import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { AssetManagement, Header } from '@aptsi-types';
import { ActivatedRoute, Router } from '@angular/router';
import { DataAssetConfigurationService } from './services/data-asset-configuration';
import {
  WorkflowNavigationService
} from '../shared/workflow-navigation.service';
import { ConfigureDataAssetByIdComponent } from './configure-data-asset-by-id/configure-data-asset-by-id.component';

type AssetRecord = AssetManagement & Record<string, unknown>;

interface ConfigurationPaginationMeta {
  totalDataAssets?: number;
  totalPages?: number;
  currentPage?: number;
}

interface NormalizedResponse {
  rows: AssetManagement[];
  metadata?: ConfigurationPaginationMeta;
}


@Component({
  selector: 'app-data-asset-configuraion',
  templateUrl: './data-asset-configuraion.component.html',
  styleUrls: ['./data-asset-configuraion.component.scss'],
})
export class DataAssetConfiguraionComponent implements OnInit {
  readonly itemsPerPage = 10;
  currentPage = 1;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;
  hasDataAssets = false;
  issearch = false;
  searchText = '';
  filterByHeader = 'all';

  filteredData: AssetManagement[] = [];
  searchedData: AssetManagement[] = [];

  private cachedSource: AssetManagement[] | null = null;
  private cachedTableData: AssetManagement[] = [];
  /** Ensures stale pagination/search requests cannot overwrite newer results. */
  private paginationRequestSequence = 0;

  readonly headers: Header[] = [
    {
      name: 'Data Asset Name',
      property: 'assetName',
      showInDropdown: true,
      description: 'Name of the data asset',
    },
    {
      name: 'Description',
      property: 'assetDescription',
      showInDropdown: true,
      description: 'Description of the data asset',
    },
       {
      name: 'Configuration Status',
      property: 'configurationStatus',
      showInDropdown: true,
      description: 'Whether the data asset is configured',
    }
 
  ];

  readonly buttonConfig = {
    update: false,
    delete: false,
    import: false,
    configure: true,
    view: true,
  };

  // State for the right-side View details panel.
  isViewPanelOpen = false;
  isViewLoading = false;
  viewLoadError = false;
  viewAssetDetails: AssetManagement | null = null;
  private activeViewRequestId = 0;

  constructor(
    private readonly dataAssetConfigurationService: DataAssetConfigurationService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly workflowNavigation:WorkflowNavigationService
  ) {}

  ngOnInit(): void {
    void this.fetchDataAssetConfigurations();
  }

  get displayedData(): AssetManagement[] {
    return this.issearch ? this.searchedData : this.filteredData;
  }

  get currentTotalItems(): number {
    return this.issearch ? this.totalItemsSearch : this.totalItemsNormal;
  }

  get currentTotalPages(): number {
    return this.issearch ? this.totalPagesSearch : this.totalPagesNormal;
  }

  /** app-main-table reads values by header.property; normalize empty fields for display. */
  get tableData(): AssetManagement[] {
    const source = this.displayedData;
    if (source === this.cachedSource) return this.cachedTableData;

    this.cachedTableData = source.map((asset) => {
      const row: Record<string, unknown> = { ...(asset as AssetRecord) };
      for (const header of this.headers) {
        const value = row[header.property];
        row[header.property] = value === null || value === undefined || String(value).trim() === ''
          ? '—'
          : value;
      }
      return row as unknown as AssetManagement;
    });
    this.cachedSource = source;
    return this.cachedTableData;
  }

  async fetchDataAssetConfigurations(): Promise<void> {
    const requestId = ++this.paginationRequestSequence;
    this.isLoading = true;
    this.isSearching = false;
    this.loadError = false;
    this.searchError = false;

    try {
      const apiResponse: unknown = await 
        this.dataAssetConfigurationService.FetchDataAssetConfigurations(this.currentPage - 1).toPromise()

      // Ignore responses from an older page request or a search that has since started.
      if (requestId !== this.paginationRequestSequence) return;

      const normalized = this.normalizeResponse(apiResponse);
      this.filteredData = normalized.rows;
      this.totalItemsNormal = this.readNonNegativeNumber(
        normalized.metadata?.totalDataAssets,
        normalized.rows.length,
      );
      this.hasDataAssets = this.totalItemsNormal > 0;

      // Pagination metadata comes from the backend. Do not calculate page totals locally.
      this.totalPagesNormal = this.readNonNegativeNumber(normalized.metadata?.totalPages, 0);

      if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesNormal) {
        // The available page count may shrink after a deletion or data change.
        this.currentPage = this.totalPagesNormal;
        this.cachedSource = null;
        await this.fetchDataAssetConfigurations();
        return;
      }

      this.cachedSource = null;
    } catch (error) {
      if (requestId !== this.paginationRequestSequence) return;

      console.error('Error fetching data asset configurations:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalItemsNormal = 0;
      this.totalPagesNormal = 0;
      this.hasDataAssets = false;
      this.cachedSource = null;
    } finally {
      if (requestId === this.paginationRequestSequence) {
        this.isLoading = false;
        this.isSearching = false;
        this.refreshView();
      }
    }
  }

  async searchDataAssetConfigurations(): Promise<void> {
    const query = this.searchText.trim();
    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.issearch = true;
    this.currentPage = 1;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.searchedData = [];
    await this.fetchDataAssetConfigurationsSearch();
  }

  async fetchDataAssetConfigurationsSearch(): Promise<void> {
    if (!this.searchText.trim()) {
      await this.clearSearch();
      return;
    }

    const requestId = ++this.paginationRequestSequence;
    this.isSearching = true;
    this.isLoading = false;
    this.searchError = false;

    try {
      const apiResponse: unknown = await 
        this.dataAssetConfigurationService.FetchDataAssetConfigurationsSearch(
          this.currentPage - 1,
          this.searchText.trim(),
          this.filterByHeader,
        ).toPromise()
  

      // Ignore responses from an older search or page request.
      if (requestId !== this.paginationRequestSequence) return;

      const normalized = this.normalizeResponse(apiResponse);
      this.searchedData = normalized.rows;
      this.totalItemsSearch = this.readNonNegativeNumber(
        normalized.metadata?.totalDataAssets,
        normalized.rows.length,
      );

      // Trust the backend's totalPages for search results as well.
      this.totalPagesSearch = this.readNonNegativeNumber(normalized.metadata?.totalPages, 0);

      if (this.totalPagesSearch === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesSearch) {
        this.currentPage = this.totalPagesSearch;
        this.cachedSource = null;
        await this.fetchDataAssetConfigurationsSearch();
        return;
      }

      this.cachedSource = null;
    } catch (error) {
      if (requestId !== this.paginationRequestSequence) return;

      console.error('Error searching data asset configurations:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      this.cachedSource = null;
    } finally {
      if (requestId === this.paginationRequestSequence) {
        this.isSearching = false;
        this.isLoading = false;
        this.refreshView();
      }
    }
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.issearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.searchedData = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    await this.fetchDataAssetConfigurations();
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.searchedData = [];

    if (this.searchText.trim()) {
      this.issearch = true;
      void this.fetchDataAssetConfigurationsSearch();
    } else {
      this.issearch = false;
      void this.fetchDataAssetConfigurations();
    }
  }

  changePage(page: number): void {
    const activeTotalPages = this.issearch ? this.totalPagesSearch : this.totalPagesNormal;
    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    if (this.issearch) void this.fetchDataAssetConfigurationsSearch();
    else void this.fetchDataAssetConfigurations();
  }

  retryLoad(): void {
    if (this.issearch && this.searchText.trim()) {
      void this.fetchDataAssetConfigurationsSearch();
    } else {
      void this.fetchDataAssetConfigurations();
    }
  }

  handleAction(event: { action: string; rowIndex: number; rowData: AssetManagement }): void {
    const assetId = String(event.rowData?.assetId ?? '').trim();

    if (event.action === 'view') {
      if (!assetId) {
        console.error('Cannot view data asset: assetId is missing.', event.rowData);
        return;
      }
      void this.openViewPanel(assetId);
      return;
    }

    if (event.action !== 'configure') return;
    if (!assetId) {
      console.error('Cannot configure data asset: assetId is missing.', event.rowData);
      return;
    }

    // void this.router.navigate([
    //   'asset-management/auth-tools/data-asset-configuration/configure-data-asset',
    //   assetId,
    //   String(event.rowData.configurationStatus ?? 'NOT_CONFIGURED'),
    // ]);

    console.log('Navigating to configure data asset:', assetId, event.rowData.configurationStatus);

     void this.router.navigate(['configure-data-asset', assetId, String(event.rowData.configurationStatus ?? 'NOT_CONFIGURED')], {
      relativeTo: this.route,
    });

  //    void this.workflowNavigation.navigateToWorkflow(
  //   'systemAssetCapture'
  // );

    // {
    //   path: 'configure-data-asset/:assetsourceId/:configurationStatus',
    //   component: ConfigureDataAssetByIdComponent
    // }
  }

  /** Call the API for the complete record, then show it in a right-side drawer. */
  async openViewPanel(dataAssetId: string): Promise<void> {
    this.isViewPanelOpen = true;
    this.isViewLoading = true;
    this.viewLoadError = false;
    this.viewAssetDetails = null;
    const requestId = ++this.activeViewRequestId;
    this.refreshView();

    try {
      // The supplied API example returns an object although the service is array-typed.
      const response: unknown = await 
        this.dataAssetConfigurationService.FetchDataAssetConfigurationById(dataAssetId).toPromise()
      if (requestId !== this.activeViewRequestId) return;
      this.viewAssetDetails = this.normalizeDetailsResponse(response);
      if (!this.viewAssetDetails) this.viewLoadError = true;
    } catch (error) {
      if (requestId !== this.activeViewRequestId) return;
      console.error('Error fetching data asset details:', error);
      this.viewLoadError = true;
    } finally {
      if (requestId === this.activeViewRequestId) {
        this.isViewLoading = false;
        this.refreshView();
      }
    }
  }

  closeViewPanel(): void {
    this.isViewPanelOpen = false;
    this.activeViewRequestId++;
    this.isViewLoading = false;
    this.viewLoadError = false;
  }

  @HostListener('document:keydown.escape')
  onViewPanelEscape(): void {
    if (this.isViewPanelOpen) this.closeViewPanel();
  }

  displayValue(value: unknown): string {
    if (value === null || value === undefined || String(value).trim() === '') return '—';
    return String(value);
  }

  displayValues(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (Array.isArray(value)) {
    const values = value
      .map((item: unknown) => String(item ?? '').trim())
      .filter((item: string) => item.length > 0);

    return values.length > 0 ? values.join(', ') : '—';
  }

  return this.displayValue(value);
}

  formatDate(value: unknown): string {
    if (value === null || value === undefined || String(value).trim() === '') return '—';
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  }

  private normalizeDetailsResponse(raw: unknown): AssetManagement | null {
    if (!raw || typeof raw !== 'object') return null;

    if (Array.isArray(raw)) {
      const first = raw.find((item) => !!item && typeof item === 'object');
      return first ? (first as AssetManagement) : null;
    }

    const record = raw as Record<string, unknown>;
    for (const key of ['data', 'configuration', 'result', 'asset']) {
      const nested = record[key];
      if (nested && typeof nested === 'object') {
        if (Array.isArray(nested)) {
          const first = nested.find((item) => !!item && typeof item === 'object');
          if (first) return first as AssetManagement;
        } else {
          return nested as AssetManagement;
        }
      }
    }
    return record as AssetManagement;
  }

  captureDataAssets(): void {
     void this.workflowNavigation.navigateToWorkflow(
    'dataAssetCapture'
  );
  }


  trackByAssetId = (index: number, asset: AssetManagement): string | number => {
    return String(asset.assetId ?? asset.configurationId ?? index);
  };

  private normalizeResponse(raw: unknown): NormalizedResponse {
    if (Array.isArray(raw)) {
      const rows = raw.filter((item): item is AssetManagement => !!item && typeof item === 'object');
      return { rows, metadata: this.findMetadata(rows) };
    }

    if (raw && typeof raw === 'object') {
      const wrapper = raw as Record<string, unknown>;
      for (const key of ['data', 'items', 'content', 'results', 'assets', 'records', 'assetview']) {
        const value = wrapper[key];
        if (Array.isArray(value)) {
          const rows = value.filter((item): item is AssetManagement => !!item && typeof item === 'object');
          return { rows, metadata: (wrapper['metadata'] ?? wrapper['meta'] ?? this.findMetadata(rows)) as ConfigurationPaginationMeta | undefined };
        }
      }
    }

    console.warn('[DataAssetConfiguraion] Unrecognized API response structure:', raw);
    return { rows: [] };
  }

  private findMetadata(rows: AssetManagement[]): ConfigurationPaginationMeta | undefined {
    const row = rows.find((item) => {
      const record = item as AssetRecord;
      return record['totalDataAssets'] !== undefined || record['totalPages'] !== undefined || record['currentPage'] !== undefined;
    });
    return row as ConfigurationPaginationMeta | undefined;
  }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private refreshView(): void {
    this.changeDetector.detectChanges();
  }
}
